import {
  duckDbIdentifier,
  readNumber,
  readString,
} from '@cotera/client/app/etc/duckdb';
import { datasetPreviewDuckDb } from '@cotera/client/app/etc/duckdb/preview-db';
import { dataGridColumnTypeFromSqlType } from '@cotera/client/app/components/ui/data-grid';
import type {
  DataGridColumnStats,
  DataGridStatBucket,
} from '@cotera/client/app/components/ui/data-grid';

const STATS_BUCKET_COUNT = 12;
const STATS_CATEGORICAL_MAX_CARDINALITY = 50;
// A column with few distinct values is only treated as a categorical/enum
// (dropdown editor, category distribution) if its values are short enough to be
// labels. Long free-text blobs (e.g. an LLM-generated "reason" paragraph) can
// have low cardinality on a small dataset but are not categories, so once the
// average value gets this long we fall back to a plain text summary instead.
const STATS_CATEGORICAL_MAX_AVG_LENGTH = 80;

const epochToIso = (epochSeconds: number): string =>
  new Date(epochSeconds * 1000).toISOString();

const bucketRanges = (
  min: number,
  max: number
): { width: number; bucketCount: number } => {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
    return { width: 1, bucketCount: 1 };
  }
  return {
    width: (max - min) / STATS_BUCKET_COUNT,
    bucketCount: STATS_BUCKET_COUNT,
  };
};

const histogramBuckets = ({
  rows,
  min,
  width,
  bucketCount,
  label,
  filterValue,
}: {
  rows: Record<string, unknown>[];
  min: number;
  width: number;
  bucketCount: number;
  label: (start: number, end: number) => string;
  filterValue: (
    start: number,
    end: number,
    isLast: boolean
  ) => DataGridStatBucket['filter'];
}): DataGridStatBucket[] => {
  const counts = new Map<number, number>();
  for (const row of rows) {
    const bucket = readNumber(row, 'bucket');
    const count = readNumber(row, 'bucket_count');
    if (bucket !== null && count !== null) {
      counts.set(bucket, count);
    }
  }
  return Array.from({ length: bucketCount }, (_, index) => {
    const start = min + index * width;
    const end = start + width;
    const isLast = index === bucketCount - 1;
    return {
      key: `bucket-${index}`,
      label: label(start, end),
      count: counts.get(index) ?? 0,
      filter: filterValue(start, end, isLast),
    };
  });
};

const numericColumnStats = async ({
  sourceSql,
  identifier,
}: {
  sourceSql: string;
  identifier: string;
}): Promise<DataGridColumnStats> => {
  const db = await datasetPreviewDuckDb;
  // TRY_CAST rather than the bare column: the artifact's declared type and the
  // parquet column's physical type do not always agree, and a binder error
  // here would take out the whole header.
  const numeric = `TRY_CAST(${identifier} AS DOUBLE)`;
  const summaryTable = await db.rawQuery(
    `SELECT min(${numeric}) AS min_value, max(${numeric}) AS max_value,
            avg(${numeric}) AS mean_value,
            count(*) FILTER (WHERE ${numeric} IS NULL) AS null_count
     FROM ${sourceSql} AS dataset_stats`
  );
  const summary = summaryTable.toArray()[0] as
    | Record<string, unknown>
    | undefined;
  const min = readNumber(summary, 'min_value');
  const max = readNumber(summary, 'max_value');
  const mean = readNumber(summary, 'mean_value') ?? 0;
  const nullCount = readNumber(summary, 'null_count') ?? 0;
  if (min === null || max === null) {
    return { kind: 'textSummary', uniqueCount: 0, nullCount, totalCount: 0 };
  }

  const { width, bucketCount } = bucketRanges(min, max);
  const bucketTable = await db.rawQuery(
    `SELECT CAST(least(${
      bucketCount - 1
    }, floor((${numeric} - ${min}) / ${width})) AS INTEGER) AS bucket,
            count(*) AS bucket_count
     FROM ${sourceSql} AS dataset_stats
     WHERE ${numeric} IS NOT NULL
     GROUP BY 1 ORDER BY 1`
  );

  return {
    kind: 'numericDistribution',
    min,
    max,
    mean,
    nullCount,
    buckets: histogramBuckets({
      rows: bucketTable.toArray() as Record<string, unknown>[],
      min,
      width,
      bucketCount,
      label: (start, end) =>
        `${start.toLocaleString(undefined, {
          maximumFractionDigits: 2,
        })} – ${end.toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
      filterValue: (start, end, isLast) => ({
        kind: 'between',
        min: start,
        max: end,
        inclusiveMax: isLast,
      }),
    }),
  };
};

const temporalColumnStats = async ({
  sourceSql,
  identifier,
}: {
  sourceSql: string;
  identifier: string;
}): Promise<DataGridColumnStats> => {
  const db = await datasetPreviewDuckDb;
  const cast = `TRY_CAST(${identifier} AS TIMESTAMP)`;
  const summaryTable = await db.rawQuery(
    `SELECT epoch(min(${cast})) AS min_epoch, epoch(max(${cast})) AS max_epoch,
            CAST(min(${cast}) AS VARCHAR) AS min_label,
            CAST(max(${cast}) AS VARCHAR) AS max_label,
            count(*) FILTER (WHERE ${cast} IS NULL) AS null_count
     FROM ${sourceSql} AS dataset_stats`
  );
  const summary = summaryTable.toArray()[0] as
    | Record<string, unknown>
    | undefined;
  const minEpoch = readNumber(summary, 'min_epoch');
  const maxEpoch = readNumber(summary, 'max_epoch');
  const nullCount = readNumber(summary, 'null_count') ?? 0;
  if (minEpoch === null || maxEpoch === null) {
    return { kind: 'textSummary', uniqueCount: 0, nullCount, totalCount: 0 };
  }

  const { width, bucketCount } = bucketRanges(minEpoch, maxEpoch);
  const bucketTable = await db.rawQuery(
    `SELECT CAST(least(${
      bucketCount - 1
    }, floor((epoch(${cast}) - ${minEpoch}) / ${width})) AS INTEGER) AS bucket,
            count(*) AS bucket_count
     FROM ${sourceSql} AS dataset_stats
     WHERE ${cast} IS NOT NULL
     GROUP BY 1 ORDER BY 1`
  );

  return {
    kind: 'temporalDistribution',
    min: readString(summary, 'min_label') ?? epochToIso(minEpoch),
    max: readString(summary, 'max_label') ?? epochToIso(maxEpoch),
    nullCount,
    buckets: histogramBuckets({
      rows: bucketTable.toArray() as Record<string, unknown>[],
      min: minEpoch,
      width,
      bucketCount,
      label: (start, end) =>
        `${epochToIso(start).slice(0, 10)} – ${epochToIso(end).slice(0, 10)}`,
      filterValue: (start, end, isLast) => ({
        kind: 'between',
        min: epochToIso(start),
        max: epochToIso(end),
        inclusiveMax: isLast,
      }),
    }),
  };
};

const categoricalColumnStats = async ({
  sourceSql,
  identifier,
}: {
  sourceSql: string;
  identifier: string;
}): Promise<DataGridColumnStats> => {
  const db = await datasetPreviewDuckDb;
  const [summaryTable, topTable] = await Promise.all([
    db.rawQuery(
      `SELECT count(*) AS total_count,
              count(*) FILTER (WHERE ${identifier} IS NULL) AS null_count,
              count(DISTINCT ${identifier}) AS unique_count,
              avg(length(${identifier}::VARCHAR)) AS avg_length
       FROM ${sourceSql} AS dataset_stats`
    ),
    // Fetch every category up to the cardinality cap (not just the top few):
    // the header shows a handful inline, but the "+ N more" popover renders the
    // full breakdown, so the buckets need to be complete. The GROUP BY already
    // aggregates all groups; only the row LIMIT changes, so this is not a
    // meaningful cost over fetching the top values.
    db.rawQuery(
      `SELECT ${identifier}::VARCHAR AS value, count(*) AS value_count
       FROM ${sourceSql} AS dataset_stats
       WHERE ${identifier} IS NOT NULL
       GROUP BY 1 ORDER BY value_count DESC, value ASC
       LIMIT ${STATS_CATEGORICAL_MAX_CARDINALITY}`
    ),
  ]);
  const summary = summaryTable.toArray()[0] as
    | Record<string, unknown>
    | undefined;
  const totalCount = readNumber(summary, 'total_count') ?? 0;
  const nullCount = readNumber(summary, 'null_count') ?? 0;
  const uniqueCount = readNumber(summary, 'unique_count') ?? 0;
  const avgLength = readNumber(summary, 'avg_length');
  const topRows = topTable.toArray() as Record<string, unknown>[];
  const buckets: DataGridStatBucket[] = topRows.flatMap((row) => {
    const value = readString(row, 'value');
    const count = readNumber(row, 'value_count');
    if (value === null || count === null) {
      return [];
    }
    return [
      {
        key: value,
        label: value,
        count,
        share: totalCount === 0 ? 0 : count / totalCount,
        filter: { kind: 'in' as const, values: [value] },
      },
    ];
  });

  const looksLikeFreeText =
    avgLength !== null && avgLength > STATS_CATEGORICAL_MAX_AVG_LENGTH;
  if (uniqueCount > STATS_CATEGORICAL_MAX_CARDINALITY || looksLikeFreeText) {
    return {
      kind: 'textSummary',
      uniqueCount,
      nullCount,
      totalCount,
      samples: buckets.slice(0, 2).map((bucket) => bucket.label),
    };
  }

  const covered = buckets.reduce((sum, bucket) => sum + bucket.count, 0);
  return {
    kind: 'categorical',
    buckets,
    uniqueCount,
    nullCount,
    otherCount: Math.max(0, totalCount - nullCount - covered),
  };
};

export const datasetColumnStats = async ({
  sourceSql,
  columnName,
  columnType,
}: {
  sourceSql: string;
  columnName: string;
  columnType: string | null;
}): Promise<DataGridColumnStats> => {
  const identifier = duckDbIdentifier(columnName);
  const type = dataGridColumnTypeFromSqlType(columnType);
  switch (type) {
    case 'number':
      return numericColumnStats({ sourceSql, identifier });
    case 'date':
    case 'timestamp':
      return temporalColumnStats({ sourceSql, identifier });
    default:
      return categoricalColumnStats({ sourceSql, identifier });
  }
};
